import React, { useContext, useEffect, useLayoutEffect, useState } from "react";
import Spin from "components/Spin/Spin";
import s from "./merchant-profile.module.scss";
import { useTranslation } from "react-i18next";
import AnalyticsTable from "components/AnalyticsTable/AnalyticsTable";
import TextMAS from "components/TextMAS/TextMAS";
import { fetchMerchantProfile } from "./model";
import { useAuth } from "utils/useAuth/useAuth";
import { PortalContext } from "App";

import useSnackbarMAS from "utils/useSnackbarMAS/useSnackbarMAS";
const MerchantProfile = ({ path, redirectFn }) => {
  const { logout } = useAuth();
  const stateContext = useContext(PortalContext);
  const { detailPageState } = stateContext;
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [merchantProfile, setMerchantProfile] = useState({});
  const [last7DaysTransaction, setLast7DaysTransaction] = useState([]);
  const [last12MonthsTransaction, setLast12MonthsTransaction] = useState([]);
  const { snackbarError } = useSnackbarMAS();

  const [merchantId, setMerchantId] = useState("");

  useLayoutEffect(() => {
    const sessionState = JSON.parse(sessionStorage.getItem("state"));
    if (detailPageState && detailPageState[path]) {
      setMerchantId(detailPageState[path].merchantId);
    } else if (sessionState && sessionState[path]) {
      setMerchantId(sessionState[path].merchantId);
    } else {
      if (merchantId == "") setMerchantId("close");
    }
  }, [detailPageState[path]]);

  useEffect(() => {
    if (merchantId == "close") {
      redirectFn("close", { tabId: path });
    }
  }, [merchantId]);

  const getMerchantProfile = async () => {
    setIsLoading(true);
    // Temporary hardcoding is in place and should be replaced once the merchant listing page development is finished.
    const apiResponse = await fetchMerchantProfile(merchantId); // offline dev merchant
    // const apiResponse = await fetchMerchantProfile(9000040); // online dev merchant
    if (!apiResponse || !apiResponse?.data) {
      snackbarError({
        message: t("general.message.networkError"),
      });
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbarError({
            message: t("general.message.unauthorized"),
          });
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            setMerchantProfile(responseBody?.data);
            setLast7DaysTransaction(responseBody?.data?.latest7DaysTransaction);
            setLast12MonthsTransaction(
              responseBody?.data?.latest12MonthsTransaction,
            );
          } else {
          }
        }
      }
    }

    setIsLoading(false);
  };

  useLayoutEffect(() => {
    if (merchantId) {
      getMerchantProfile();
    }
  }, [merchantId]);

  const round2digit = (num) => {
    return Number(num).toFixed(2);
  };

  const renderMonthlyTransactionChangeCell = (
    number,
    change,
    changePercentage,
  ) => {
    const statChangeColorClass =
      Number(change) === 0
        ? s["change-stat-grey"]
        : Number(change) > 0
          ? s["change-stat-green"]
          : s["change-stat-red"];
    return (
      <div className={s["transactionItemContainer"]}>
        <div className={s["transactionItemValue"]}>
          <TextMAS
            className={
              s["transactionItemText"] +
              ` ${Number(change) < 0 ? statChangeColorClass : ""}`
            }
          >
            {round2digit(number)}
          </TextMAS>
        </div>
        <div className={s["transactionItemChange"]}>
          <div className={s["transactionItemChangeAmount"]}>
            <TextMAS fontSize="small" className={statChangeColorClass}>
              {Number(change) > 0 ? "+" : ""}
              {round2digit(change)}
            </TextMAS>
          </div>
          <div>
            <TextMAS fontSize="small" className={statChangeColorClass}>
              ({Number(change) > 0 ? "+" : ""}
              {round2digit(changePercentage)}% )
            </TextMAS>
          </div>
        </div>
      </div>
    );
  };

  const renderDailyTransactionChangeCell = (number, change) => {
    const statChangeColorClass = Number(change) < 0 ? s["change-stat-red"] : "";
    return (
      <div className={s["transactionItemContainer"]}>
        <TextMAS
          className={s["dailyTransactionItemText"] + statChangeColorClass}
        >
          {round2digit(number)}
        </TextMAS>
      </div>
    );
  };

  const last12MonthsTransactionColumns = [
    {
      id: "month",
      title: "Month",
      key: "month",
      headerStyle: { width: "80px" },
      sorting: "true",
    },
    {
      id: "total_amount",
      title: "Volume",
      key: "total_amount",
      headerStyle: { width: "160px" },
      sorting: "true",
      render: ({ data }) => {
        return renderMonthlyTransactionChangeCell(
          data.total_amount,
          data.change_amount,
          data.change_amount_percentage,
        );
      },
    },
    {
      id: "count",
      title: "Count",
      key: "count",
      headerStyle: { width: "160px" },
      sorting: "true",
      render: ({ data }) => {
        return renderMonthlyTransactionChangeCell(
          data.count,
          data.change_count,
          data.change_count_percentage,
        );
      },
    },
  ];

  const last7DaysTransactionColumns = [
    {
      id: "date",
      title: "Date",
      key: "date",
      headerStyle: { width: "80px" },
      sorting: "true",
    },
    {
      id: "total_amount",
      title: "Volume",
      key: "total_amount",
      headerStyle: { width: "160px" },
      sorting: "true",
      render: ({ data }) => {
        return renderDailyTransactionChangeCell(
          data.total_amount,
          data.change_amount,
        );
      },
    },
    {
      id: "count",
      title: "Count",
      key: "count",
      headerStyle: { width: "160px" },
      sorting: "true",
      render: ({ data }) => {
        return renderDailyTransactionChangeCell(data.count, data.change_count);
      },
    },
  ];

  return (
    <div className={s["container"]}>
      {isLoading ? (
        <div className={s["loadingNow"]}>
          <Spin />
        </div>
      ) : (
        <>
          <div className={s["merchantProfileTitleContainer"]}>
            <TextMAS
              className={s["merchantProfileTitle"]}
              fontSize="large"
              fontWeight="semibold"
            >
              MID {merchantProfile.merchant_id} - {merchantProfile.name}
            </TextMAS>
          </div>
          <div className={s["merchantInfoItemContainer"]}>
            <div className={s["merchantInfoItem"]}>
              <TextMAS className={s["merchantInfoItemTitle"]}>DBA name</TextMAS>
              <TextMAS className={s["merchantInfoItemValue"]}>
                {merchantProfile.dba_name}
              </TextMAS>
            </div>
            <div className={s["merchantInfoItem"]}>
              <TextMAS className={s["merchantInfoItemTitle"]}>MCC</TextMAS>
              <TextMAS className={s["merchantInfoItemValue"]}>
                {merchantProfile.mcc}
              </TextMAS>
            </div>
            <div className={s["merchantInfoItem"]}>
              <TextMAS className={s["merchantInfoItemTitle"]}>
                Business Plan
              </TextMAS>
              <TextMAS className={s["merchantInfoItemValue"]}>
                {merchantProfile.business_plan}
              </TextMAS>
            </div>
            <div className={s["merchantInfoItem"]}>
              <TextMAS className={s["merchantInfoItemTitle"]}>Sales</TextMAS>
              <TextMAS className={s["merchantInfoItemValue"]}>
                {merchantProfile.sales}
              </TextMAS>
            </div>
            <div className={s["merchantInfoItem"]}>
              <TextMAS className={s["merchantInfoItemTitle"]}>Referrer</TextMAS>
              <TextMAS className={s["merchantInfoItemValue"]}>
                {merchantProfile.referrer}
              </TextMAS>
            </div>
            <div className={s["merchantInfoItem"]}>
              <TextMAS className={s["merchantInfoItemTitle"]}>
                System Integrator
              </TextMAS>
              <TextMAS className={s["merchantInfoItemValue"]}>
                {merchantProfile.system_integrator}
              </TextMAS>
            </div>
            <div className={s["merchantInfoItem"]}>
              <TextMAS className={s["paymentMethodContainerTitle"]}>
                Payment Methods
              </TextMAS>
              <div className={s["paymentMethodContainer"]}>
                <div className={s["paymentMethodItem"]}>
                  <TextMAS className={s["merchantInfoItemTitle"]}>Visa</TextMAS>
                  <TextMAS className={s["paymentMethodValue"]}>
                    {merchantProfile.payment_methods_visa}
                  </TextMAS>
                </div>
                <div className={s["paymentMethodItem"]}>
                  <TextMAS>Mastercard</TextMAS>
                  <TextMAS className={s["paymentMethodValue"]}>
                    {merchantProfile.payment_methods_mastercard}
                  </TextMAS>
                </div>
                <div className={s["paymentMethodItem"]}>
                  <TextMAS>Unionpay</TextMAS>
                  <TextMAS className={s["paymentMethodValue"]}>
                    {merchantProfile.payment_methods_unionpay}
                  </TextMAS>
                </div>
                <div className={s["paymentMethodItem"]}>
                  <TextMAS>Amex</TextMAS>
                  <TextMAS className={s["paymentMethodValue"]}>
                    {merchantProfile.payment_methods_amex}
                  </TextMAS>
                </div>
                <div className={s["paymentMethodItem"]}>
                  <TextMAS>Alipay</TextMAS>
                  <TextMAS className={s["paymentMethodValue"]}>
                    {merchantProfile.payment_methods_alipay}
                  </TextMAS>
                </div>
                <div className={s["paymentMethodItem"]}>
                  <TextMAS>Wechatpay</TextMAS>
                  <TextMAS className={s["paymentMethodValue"]}>
                    {merchantProfile.payment_methods_wechat}
                  </TextMAS>
                </div>
              </div>
            </div>
          </div>
          <div className={s["merchantInfoItemContainer"]}>
            <div className={s["merchantProfileSubtitle"]}>
              <TextMAS
                className={s["merchantProfileSubtitle"]}
                fontSize="large"
                fontWeight="semibold"
              >
                Statistics
              </TextMAS>
            </div>
            <div className={s["merchantInfoItem"]}>
              <TextMAS className={s["merchantInfoItemTitle"]}>
                Last Active Date
              </TextMAS>
              <TextMAS className={s["merchantInfoItemValue"]}>
                {merchantProfile.last_active_date}
              </TextMAS>
            </div>
            <div className={s["merchantInfoItem"]}>
              <TextMAS className={s["merchantInfoItemTitle"]}>
                Account Creation Date
              </TextMAS>
              <TextMAS className={s["merchantInfoItemValue"]}>
                {merchantProfile.account_creation_date}
              </TextMAS>
            </div>
            <div className={s["merchantInfoItem"]}>
              <TextMAS className={s["merchantInfoItemTitle"]}>
                Account Age
              </TextMAS>
              <TextMAS className={s["merchantInfoItemValue"]}>
                {merchantProfile.account_age}
              </TextMAS>
            </div>
            <div className={s["merchantInfoItem"]}>
              <TextMAS className={s["merchantInfoItemTitle"]}>
                Average Monthly Transaction Volume (Last 12 Months)
              </TextMAS>
              <TextMAS className={s["merchantInfoItemValue"]}>
                {
                  merchantProfile.last_12_month_average_monthly_transaction_volume
                }
              </TextMAS>
            </div>
            <div className={s["merchantInfoItem"]}>
              <TextMAS className={s["merchantInfoItemTitle"]}>
                Average Monthly Transaction Count (Last 12 Months)
              </TextMAS>
              <TextMAS className={s["merchantInfoItemValue"]}>
                {
                  merchantProfile.last_12_month_average_monthly_transaction_count
                }
              </TextMAS>
            </div>
            <div className={s["merchantInfoItem"]}>
              <TextMAS className={s["merchantInfoItemTitle"]}>
                Average Ticket Size (Last 12 Months)
              </TextMAS>
              <TextMAS className={s["merchantInfoItemValue"]}>
                {merchantProfile.last_12_month_average_ticket_size}
              </TextMAS>
            </div>
            <div className={s["merchantInfoItem"]}>
              <TextMAS className={s["merchantInfoItemTitle"]}>
                Maximum Ticket
              </TextMAS>
              <TextMAS className={s["merchantInfoItemValue"]}>
                {merchantProfile.maximum_ticket}
              </TextMAS>
            </div>
            <div className={s["merchantInfoItem"]}>
              <TextMAS className={s["merchantInfoItemTitle"]}>
                Minimum Ticket
              </TextMAS>
              <TextMAS className={s["merchantInfoItemValue"]}>
                {merchantProfile.minimum_ticket}
              </TextMAS>
            </div>
          </div>
          <div className={s["merchantInfoItemContainer"]}>
            <div className={s["merchantProfileSubtitle"]}>
              <TextMAS
                className={s["merchantProfileSubtitle"]}
                fontSize="large"
                fontWeight="semibold"
              >
                Transactions By Month (Last 12 Months)
              </TextMAS>
            </div>
            <AnalyticsTable
              columns={last12MonthsTransactionColumns}
              data={last12MonthsTransaction}
            />
          </div>
          <div
            className={
              s["merchantInfoItemContainer"] +
              ` ${s["merchantProfilePageOffset"]}`
            }
          >
            <div className={s["merchantProfileSubtitle"]}>
              <TextMAS
                className={s["merchantProfileSubtitle"]}
                fontSize="large"
                fontWeight="semibold"
              >
                Transactions By Day (Last 7 days)
              </TextMAS>
            </div>
            <AnalyticsTable
              columns={last7DaysTransactionColumns}
              data={last7DaysTransaction}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default React.memo(MerchantProfile);
